import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useToast } from '@/components/ui/Toast';
import { onPlatformForbidden } from '@/lib/apiClient';
import { forbiddenAbility } from '@/lib/userFacingError';

/** Same message within this window is shown once (pages often fire several queries). */
export const FORBIDDEN_TOAST_DEDUPE_MS = 5000;

export function forbiddenToastMessage(error: unknown, t: TFunction): string {
    const ability = forbiddenAbility(error);
    return ability
        ? t('auth.forbidden.toastWithAbility', {
              defaultValue: 'Your role cannot do this ({{ability}}). Ask a platform admin if you need access.',
              ability,
          })
        : t('auth.forbidden.toast', { defaultValue: 'Your role cannot do this. Ask a platform admin if you need access.' });
}

/** Returns true when `message` should be shown now, recording it in `seen`. */
export function shouldShowForbiddenToast(seen: Map<string, number>, message: string, now: number): boolean {
    const last = seen.get(message);
    if (last !== undefined && now - last < FORBIDDEN_TOAST_DEDUPE_MS) return false;
    seen.set(message, now);
    return true;
}

/** API-10: mounted once in App; turns backend 403s into a single toast. */
export function ForbiddenToastBridge(): null {
    const { t } = useTranslation();
    const toast = useToast();
    const seen = useRef(new Map<string, number>());

    useEffect(
        () =>
            onPlatformForbidden((error) => {
                const message = forbiddenToastMessage(error, t);
                if (shouldShowForbiddenToast(seen.current, message, Date.now())) toast.error(message);
            }),
        [t, toast],
    );

    return null;
}
